// Reusable trust strip rendered ABOVE the birth-details form on conversion /
// tool / paywall surfaces. One source of truth so every surface shows the same
// defensible signals: social proof (count), privacy (birth details stay private),
// and secure checkout (Razorpay). No unverifiable accuracy claim — the precision
// signal lives on the page hero ("Swiss Ephemeris · Lahiri ayanamsa").
//
// Presentational only (no client hooks) so it renders in both server and client
// trees. pointer-events:none mirrors the dead-click discipline applied to other
// descriptive chrome (rule 32) — the badges look informative, never tappable.

interface TrustStripProps {
  language?: "en" | "hi";
  /** Drop the secure-checkout badge on pure free-tool surfaces with no paywall. */
  showCheckout?: boolean;
  className?: string;
}

type Copy = {
  socialProof: string;
  privacy: string;
  checkout: string;
};

const COPY: Record<"en" | "hi", Copy> = {
  en: {
    socialProof: "12,000+ kundalis generated",
    privacy: "Your birth details stay private",
    checkout: "Secure Razorpay checkout",
  },
  hi: {
    socialProof: "12,000+ कुंडलियाँ बनीं",
    privacy: "आपके जन्म विवरण निजी रहते हैं",
    checkout: "सुरक्षित Razorpay चेकआउट",
  },
};

type IconType = "group" | "lock" | "shield";

export default function TrustStrip({
  language = "en",
  showCheckout = true,
  className = "",
}: TrustStripProps) {
  const copy = COPY[language];
  const badges: Array<{ icon: IconType; label: string }> = [
    { icon: "group", label: copy.socialProof },
    { icon: "lock", label: copy.privacy },
  ];
  if (showCheckout) {
    badges.push({ icon: "shield", label: copy.checkout });
  }
  return (
    <div
      className={`mb-4 flex flex-wrap justify-center gap-x-4 gap-y-2 text-[11px] font-medium ${className}`}
      style={{ color: "var(--ish-text-faint)", pointerEvents: "none" }}
    >
      {badges.map((badge) => (
        <span key={badge.label} className="flex items-center gap-1.5">
          <TrustStripIcon type={badge.icon} />
          {badge.label}
        </span>
      ))}
    </div>
  );
}

function TrustStripIcon({ type }: { type: IconType }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      {type === "group" && (
        <>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </>
      )}
      {type === "lock" && (
        <>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </>
      )}
      {type === "shield" && (
        <>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M9 12l2 2 4-4" />
        </>
      )}
    </svg>
  );
}
